import { useState, useEffect, useRef } from "react";
import { login } from "./api";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #03050f;
    --ai-blue: #3b9eff;
    --ai-blue-bright: #60b8ff;
    --ai-blue-deep: #1a6fd4;
    --text: #eef4ff;
    --muted: #5a7a99;
    --muted-light: #8aabb8;
    --error: #ff6b8a;
    --success: #4affc4;
  }

  html, body, #root {
    height: 100%;
    background: var(--bg);
    color: var(--text);
    font-family: 'Outfit', sans-serif;
    overflow: hidden;
  }

  canvas.orb-canvas {
    position: fixed; inset: 0; width: 100%; height: 100%;
    pointer-events: none; z-index: 0;
  }

  .noise {
    position: fixed; inset: 0; opacity: 0.022;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
    pointer-events: none; z-index: 1;
  }

  .login-root {
    position: relative; z-index: 2; min-height: 100vh;
    display: flex; align-items: center; justify-content: center; padding: 24px;
  }

  .card {
    width: 100%; max-width: 420px;
    background: rgba(6,12,28,0.72);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 28px;
    position: relative;
    backdrop-filter: blur(40px);
    -webkit-backdrop-filter: blur(40px);
    box-shadow:
      0 0 0 1px rgba(59,158,255,0.06),
      0 40px 80px rgba(0,0,0,0.65),
      inset 0 1px 0 rgba(255,255,255,0.06);
    animation: cardIn 0.7s cubic-bezier(0.16,1,0.3,1) both;
  }

  @keyframes cardIn {
    from { opacity:0; transform:translateY(32px) scale(0.96); }
    to   { opacity:1; transform:translateY(0) scale(1); }
  }

  /* Wave — no box, bleeds freely */
  .wave-abs {
    position: absolute; top:0; left:0; right:0; height:130px;
    pointer-events: none; border-radius: 28px 28px 0 0; overflow: hidden;
  }
  .wave-abs::after {
    content:''; position:absolute; bottom:0; left:0; right:0; height:70px;
    background: linear-gradient(to bottom, transparent, rgba(6,12,28,0.72));
    pointer-events: none;
  }
  .wave-abs canvas { width:100%; height:100%; display:block; }

  .card-body { padding: 150px 40px 40px; }

  .logo-wrap { display:flex; flex-direction:column; align-items:center; margin-bottom:26px; }
  .logo-name { font-size:21px; font-weight:700; letter-spacing:-0.02em; }
  .logo-tag { font-size:11px; font-weight:500; color:var(--ai-blue); letter-spacing:0.12em; text-transform:uppercase; margin-top:3px; opacity:0.8; }

  /* Demo hint box */
  .demo-box {
    background: rgba(59,158,255,0.06); border:1px solid rgba(59,158,255,0.15);
    border-radius:10px; padding:10px 14px; margin-bottom:20px; font-size:12px;
    color:var(--muted-light); line-height:1.7;
  }
  .demo-box strong { color:var(--ai-blue-bright); }

  .admin-mode-badge {
    display:flex; align-items:center; justify-content:center; gap:8px;
    background:rgba(59,158,255,0.08); border:1px solid rgba(59,158,255,0.2);
    border-radius:10px; padding:10px 14px; margin-bottom:20px;
    font-size:12px; color:var(--ai-blue-bright); font-weight:500; letter-spacing:0.06em;
    animation:fadeSlide 0.35s ease;
  }
  @keyframes fadeSlide { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }

  .admin-dot-live { width:6px;height:6px;border-radius:50%;background:var(--ai-blue);animation:blink 1.5s ease infinite; }
  @keyframes blink{0%,100%{opacity:1}50%{opacity:0.2}}

  .form-heading { font-size:22px;font-weight:700;letter-spacing:-0.02em;margin-bottom:5px;text-align:center; }
  .form-sub { font-size:13.5px;color:var(--muted-light);text-align:center;margin-bottom:28px;font-weight:300;line-height:1.5; }

  .field { margin-bottom:15px; }
  .field label { display:block;font-size:11px;font-weight:600;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:8px; }
  .field input {
    width:100%;padding:13px 16px;background:rgba(59,158,255,0.04);
    border:1px solid rgba(255,255,255,0.07);border-radius:12px;
    color:var(--text);font-family:'Outfit',sans-serif;font-size:15px;outline:none;
    transition:border-color 0.2s,box-shadow 0.2s,background 0.2s;
  }
  .field input:focus {
    border-color:var(--ai-blue);background:rgba(59,158,255,0.07);
    box-shadow:0 0 0 3px rgba(59,158,255,0.12);
  }
  .field input::placeholder { color:rgba(90,122,153,0.5); }

  .forgot { display:block;text-align:right;font-size:12.5px;color:var(--ai-blue);cursor:pointer;margin-top:-6px;margin-bottom:24px;opacity:0.8;transition:opacity 0.2s; }
  .forgot:hover { opacity:1; }

  .submit-btn {
    width:100%;padding:14px;background:linear-gradient(135deg,var(--ai-blue-deep),var(--ai-blue));
    color:#fff;border:none;border-radius:12px;font-family:'Outfit',sans-serif;font-size:15px;
    font-weight:600;cursor:pointer;transition:all 0.25s ease;box-shadow:0 4px 20px rgba(59,158,255,0.3);
    position:relative;overflow:hidden;
  }
  .submit-btn::before { content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(255,255,255,0.1),transparent);opacity:0;transition:opacity 0.2s; }
  .submit-btn:hover::before { opacity:1; }
  .submit-btn:hover { transform:translateY(-2px);box-shadow:0 8px 32px rgba(59,158,255,0.45); }
  .submit-btn:active { transform:translateY(0); }
  .submit-btn:disabled { opacity:0.5;cursor:not-allowed;transform:none; }

  .msg { padding:11px 14px;border-radius:10px;font-size:13px;margin-bottom:16px; }
  .msg.error   { background:rgba(255,107,138,0.08);border:1px solid rgba(255,107,138,0.25);color:var(--error); }
  .msg.success { background:rgba(74,255,196,0.08);border:1px solid rgba(74,255,196,0.25);color:var(--success); }

  .back-client { display:block;text-align:center;font-size:12px;color:var(--muted);cursor:pointer;margin-top:14px;transition:color 0.2s; }
  .back-client:hover { color:var(--ai-blue); }

  .secret-zone { display:flex;flex-direction:column;align-items:center;margin-top:24px;user-select:none; }
  .secret-dots { display:flex;gap:6px;padding:8px;cursor:pointer; }
  .s-dot { width:4px;height:4px;border-radius:50%;background:rgba(90,122,153,0.3);transition:background 0.25s,transform 0.25s; }
  .secret-dots:hover .s-dot { background:rgba(59,158,255,0.35); }
  .s-dot.lit { background:var(--ai-blue)!important;transform:scale(1.5);box-shadow:0 0 6px rgba(59,158,255,0.7); }
  .secret-hint { font-family:'JetBrains Mono',monospace;font-size:10px;color:rgba(59,158,255,0.4);letter-spacing:0.1em;margin-top:4px; }

  .spinner { display:inline-block;width:15px;height:15px;border:2px solid rgba(255,255,255,0.25);border-top-color:#fff;border-radius:50%;animation:spin 0.65s linear infinite;vertical-align:middle;margin-right:8px; }
  @keyframes spin{to{transform:rotate(360deg)}}

  @media(max-width:480px){
    .card-body { padding:140px 24px 32px; }
  }
`;

/* ── Background floating orbs ── */
function OrbCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const orbs = [
      {x:0.18,y:0.28,r:340,speed:0.00035,color:"59,158,255",phase:0},
      {x:0.82,y:0.72,r:280,speed:0.00055,color:"26,109,212",phase:2},
      {x:0.50,y:0.08,r:220,speed:0.00028,color:"96,184,255",phase:4},
      {x:0.08,y:0.85,r:190,speed:0.00045,color:"15,50,130", phase:1},
    ];
    const draw = () => {
      ctx.clearRect(0,0,canvas.width,canvas.height); t++;
      orbs.forEach(o=>{
        const ox=o.x*canvas.width +Math.sin(t*o.speed+o.phase)*80;
        const oy=o.y*canvas.height+Math.cos(t*o.speed*1.3+o.phase)*60;
        const g=ctx.createRadialGradient(ox,oy,0,ox,oy,o.r);
        g.addColorStop(0,`rgba(${o.color},0.18)`);
        g.addColorStop(0.5,`rgba(${o.color},0.06)`);
        g.addColorStop(1,`rgba(${o.color},0)`);
        ctx.fillStyle=g; ctx.beginPath(); ctx.arc(ox,oy,o.r,0,Math.PI*2); ctx.fill();
      });
      frame=requestAnimationFrame(draw);
    };
    draw();
    return ()=>{ cancelAnimationFrame(frame); window.removeEventListener("resize",resize); };
  }, []);
  return <canvas ref={ref} className="orb-canvas"/>;
}

/* ── Fluid wave ── */
function WaveCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let frame, t = 0;
    const resize = () => {
      const dpr = window.devicePixelRatio||1;
      canvas.width  = canvas.offsetWidth  * dpr;
      canvas.height = canvas.offsetHeight * dpr;
    };
    resize();
    window.addEventListener("resize", resize);
    const layers = [
      {speed:0.016,amp:0.50,freq:1.6,phase:0.0,color:[10, 60,180],alpha:0.85},
      {speed:0.022,amp:0.38,freq:2.4,phase:1.2,color:[30,120,240],alpha:0.70},
      {speed:0.030,amp:0.28,freq:3.2,phase:2.6,color:[59,158,255],alpha:0.55},
      {speed:0.013,amp:0.55,freq:1.1,phase:0.7,color:[ 8, 40,140],alpha:0.60},
      {speed:0.040,amp:0.18,freq:4.5,phase:3.8,color:[96,184,255],alpha:0.38},
      {speed:0.019,amp:0.32,freq:2.0,phase:5.1,color:[20,100,220],alpha:0.60},
    ];
    const draw = () => {
      const dpr=window.devicePixelRatio||1;
      const w=canvas.width/dpr, h=canvas.height/dpr;
      ctx.save(); ctx.scale(dpr,dpr);
      ctx.clearRect(0,0,w,h);
      ctx.fillStyle="rgba(3,8,24,1)"; ctx.fillRect(0,0,w,h);
      [...layers].reverse().forEach(layer=>{
        const midY=h*0.52, ampPx=h*layer.amp;
        ctx.beginPath(); ctx.moveTo(0,h);
        for(let x=0;x<=w;x+=1.5){
          const nx=x/w;
          const y=midY
            +Math.sin(nx*Math.PI*2*layer.freq+t*layer.speed+layer.phase)*ampPx*0.55
            +Math.sin(nx*Math.PI*2*layer.freq*0.61+t*layer.speed*1.5+layer.phase+1)*ampPx*0.45;
          x===0?ctx.moveTo(x,y):ctx.lineTo(x,y);
        }
        ctx.lineTo(w,h); ctx.lineTo(0,h); ctx.closePath();
        const [r,g,b]=layer.color;
        const grad=ctx.createLinearGradient(0,0,0,h);
        grad.addColorStop(0,`rgba(${r},${g},${b},${layer.alpha})`);
        grad.addColorStop(0.6,`rgba(${r},${g},${b},${layer.alpha*0.5})`);
        grad.addColorStop(1,`rgba(${r},${g},${b},0.05)`);
        ctx.fillStyle=grad; ctx.fill();
      });
      // shimmer line
      const sA=0.2+0.1*Math.sin(t*0.018);
      const sG=ctx.createLinearGradient(0,0,w,0);
      sG.addColorStop(0,"rgba(59,158,255,0)");
      sG.addColorStop(0.35,`rgba(160,220,255,${sA})`);
      sG.addColorStop(0.65,`rgba(59,158,255,${sA*0.8})`);
      sG.addColorStop(1,"rgba(59,158,255,0)");
      const midY=h*0.52;
      ctx.beginPath();
      for(let x=0;x<=w;x+=1.5){
        const nx=x/w;
        const y=midY+Math.sin(nx*Math.PI*3.8+t*0.023)*h*0.16+Math.sin(nx*Math.PI*1.9+t*0.016)*h*0.10;
        x===0?ctx.moveTo(x,y):ctx.lineTo(x,y);
      }
      ctx.strokeStyle=sG; ctx.lineWidth=1.2; ctx.stroke();
      ctx.restore(); t++;
      frame=requestAnimationFrame(draw);
    };
    draw();
    return ()=>{ cancelAnimationFrame(frame); window.removeEventListener("resize",resize); };
  }, []);
  return <canvas ref={ref} style={{width:"100%",height:"100%",display:"block"}}/>;
}

const SECRET_CLICKS = 5;

export default function Login({ onLogin }) {
  const [isAdmin, setIsAdmin]   = useState(false);
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [clicks, setClicks]     = useState(0);
  const [showHint, setShowHint] = useState(false);
  const timerRef = useRef(null);

  const handleSecretClick = () => {
    const next = clicks + 1;
    setClicks(next);
    clearTimeout(timerRef.current);
    if (next >= SECRET_CLICKS) {
      setIsAdmin(true); setClicks(0); setShowHint(false); setError(""); setSuccess("");
    } else {
      if (next >= 2) setShowHint(true);
      timerRef.current = setTimeout(()=>{ setClicks(0); setShowHint(false); }, 3000);
    }
  };

  const handleSubmit = async () => {
    setError(""); setSuccess("");
    if (!email || !password) { setError("Please fill in all fields."); return; }

    setLoading(true);
    const role = isAdmin ? "admin" : "client";

    try {
      const response = await login(role, email, password);
      localStorage.setItem("token", response.token);
      localStorage.setItem("role", response.role);
      localStorage.setItem("username", response.username || response.email || "");
      setSuccess("Authenticated. Loading dashboard…");
      setTimeout(() => {
        onLogin?.(response.role);
      }, 800);
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <OrbCanvas/>
      <div className="noise"/>

      <div className="login-root">
        <div className="card">

          {/* Free-flowing wave at top */}
          <div className="wave-abs"><WaveCanvas/></div>

          <div className="card-body">
            {/* Brand */}
            <div className="logo-wrap">
              <div className="logo-name">Tax Automation</div>
              <div className="logo-tag">AI-Powered · Automation System</div>
            </div>

            {/* Admin badge */}
            {isAdmin && (
              <div className="admin-mode-badge">
                <div className="admin-dot-live"/>
                ADMINISTRATOR ACCESS
              </div>
            )}

            <h2 className="form-heading">{isAdmin ? "Admin Portal" : "Welcome back"}</h2>
            <p className="form-sub">
              {isAdmin
                ? "Restricted access. Authorised personnel only."
                : "Sign in to access your dashboard."}
            </p>

            {error   && <div className="msg error">⚠ {error}</div>}
            {success && <div className="msg success">✓ {success}</div>}

            <div className="field">
              <label>{isAdmin ? "Email" : "Username"}</label>
              <input
                type="text"
                placeholder={isAdmin ? "admin@vatica.ai" : "your.username"}
                value={email}
                onChange={e=>setEmail(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&handleSubmit()}/>
            </div>

            <div className="field">
              <label>Password</label>
              <input type="password" placeholder="••••••••••" value={password}
                onChange={e=>setPassword(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&handleSubmit()}/>
            </div>

            <span className="forgot">Forgot password?</span>

            <button className="submit-btn" onClick={handleSubmit} disabled={loading}>
              {loading && <span className="spinner"/>}
              {loading ? "Authenticating…" : isAdmin ? "Enter Admin Portal" : "Sign In"}
            </button>

            {isAdmin && (
              <span className="back-client" onClick={()=>{setIsAdmin(false);setError("");}}>
                ← Back to client login
              </span>
            )}

            {!isAdmin && (
              <div className="secret-zone">
                <div className="secret-dots" onClick={handleSecretClick}>
                  {[0,1,2].map(i=>(
                    <div key={i} className={`s-dot ${i<clicks?"lit":""}`}/>
                  ))}
                </div>
                {showHint && <div className="secret-hint">{SECRET_CLICKS-clicks} more</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}